import React from 'react';
import { motion } from 'framer-motion';
import { T } from '../lib/lang';

export function BehindTheScenes() {
  return (
    <section id="notes" className="border-b border-border py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-dim">
            <T ru="bonus" en="bonus" />
          </div>
          <h2 className="mt-3 font-serif text-3xl font-light italic text-text md:text-4xl">
            <T ru="закулисье · 18.12.2025" en="behind the scenes · dec 18, 2025" />
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-muted">
            <T
              ru="после эфира мы обсуждали Level 3 (IFS + AI) и то, как устроена ежедневная практика: память, системные инструкции, артефакты и визуальные метафоры."
              en="after the episode we unpacked Level 3 (IFS + AI) and the daily practice: memory, system instructions, artifacts, and visual metaphors."
            />
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          <motion.div
            className="rounded-xl border border-border bg-bg/30 p-6 backdrop-blur md:p-8"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div className="text-[10px] uppercase tracking-widest text-dim">
              <T ru="как устроен процесс" en="how the process works" />
            </div>

            <ul className="mt-5 space-y-4 text-sm leading-7 text-muted">
              <li>
                <span className="text-text">1.</span>{' '}
                <T
                  ru="достаём «топ-10 частей» (не универсальные архетипы, а твою живую карту)."
                  en="extract your “top 10 parts” (not universal archetypes, but your living map)."
                />
              </li>
              <li>
                <span className="text-text">2.</span>{' '}
                <T
                  ru="собираем about-me документ: части, противоречия, намерения, тесты."
                  en="compile an about-me file: parts, contradictions, intentions, assessments."
                />
              </li>
              <li>
                <span className="text-text">3.</span>{' '}
                <T
                  ru="подключаем его как память / системные инструкции (в ChatGPT, Cursor или любой LLM)."
                  en="attach it as memory / system instructions (ChatGPT, Cursor, or any LLM)."
                />
              </li>
              <li>
                <span className="text-text">4.</span>{' '}
                <T
                  ru="встраиваем IFS-рамку в ежедневные запросы: «какая часть говорит? что защищает? что нужно?»."
                  en="embed the IFS frame into daily prompts: “which part is speaking? protecting? needing?”."
                />
              </li>
              <li>
                <span className="text-text">5.</span>{' '}
                <T
                  ru="dogfooding: сначала на себе — потом в продукт и обучение."
                  en="dogfooding: test on yourself first — then ship and teach."
                />
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="rounded-xl border border-border bg-card/60 p-6 md:p-8"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.05, ease: 'easeOut' }}
          >
            <div className="text-[10px] uppercase tracking-widest text-dim">
              <T ru="ключевые фразы" en="key lines" />
            </div>

            <div className="mt-5 space-y-5">
              <Quote
                ru="конечный результат — не чего-то добиться, а скорее от себя отстать."
                en="the end result isn’t achievement — it’s letting yourself be."
              />
              <Quote
                ru="визуал цепляет подсознание раньше, чем рациональный мозг успевает обесценить."
                en="visuals land in the subconscious before the rational mind can dismiss them."
              />
              <Quote
                ru="контекст копится мегабайтами — и превращается в один about-me файл."
                en="context accumulates in megabytes — then becomes a single about-me file."
              />
              <Quote
                ru="в реальном времени полезны «рецепты»: вопросы, которые ещё не задали."
                en="in real time, “recipes” help: questions you haven’t asked yet."
              />
            </div>

            <div className="mt-8 text-[12px] leading-6 text-dim">
              <T
                ru="(источник: внутренний созвон после Founder OS #14, расшифровка Krisp)"
                en="(source: internal call after Founder OS #14, Krisp transcript)"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Quote({ ru, en }: { ru: string; en: string }) {
  return (
    <div className="rounded border border-border bg-bg/40 p-5">
      <div className="text-[14px] leading-7 text-text">
        <T ru={`“${ru}”`} en={`“${en}”`} />
      </div>
    </div>
  );
}


